import { Component, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Observable, Subscription } from 'rxjs';
import * as uuid from 'uuid';

import { AgentAdhocSourceResponse } from '../../../api/agents/models/curation-agent.api';
import { FilterService } from '../../../Common/Filter/services/abstract-filter.service';
import { ButtonTypesEnum } from '../../../Common/Forms/enums/button-types.enum';
import { RightSidebarComponent } from '../../../Common/Layout/components/RightSidebar/right-sidebar.component';
import { TakeOverPanelComponent } from '../../../Common/TakeOver/components/TakeOverPanel/take-over-panel.component';
import { Mention } from '../../../Common/Mentions/models/mention';
import { MentionInsightsExplorerService } from '../../../Common/Mentions/services/mention-insights-explorer.service';
import { MentionDetailsTakeOverPanelComponent } from
    '../../../Common/Mentions/components/MentionDetailsTakeOverPanel/mention-details-take-over-panel.component';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { MentionTargetingRhpContextsService }
    from '../../../Common/RhpContexts/services/mention-targeting-rhp-contexts.service';
import { TargetingRhpContextsService } from '../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { TargetingRhpDialogService } from '../../../Common/TargetingShared/services/targeting-rhp-dialog.service';
import { ToolbarStateService } from '../../../Common/TargetingShared/services/toolbar-state.service';
import { AbstractDialogComponent } from '../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { DialogIoModel } from '../../../Core/Dialogs/models/dialog-io.model';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { EntityType } from '../../../api/targeting/enum/entity.type.enum';
import { EntityTakeoverModel } from '../../../Common/RhpContexts/models/entity-takeover.model';
import { MentionSearchFacetTypesEnum as MentionSearchFacet } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { SearchModeEnum } from '../../../Common/TargetingShared/enum/search-mode.enum';
import { TargetingSearch } from '../../../Discover/Targeting/models/targeting-search.model';
import { ConfigService } from '../../../Core/Config/services/config.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { TargetingSearchService } from '../../../Discover/Targeting/services/targeting-search.service';
import { MentionSearchService } from '../../../Monitor/Core/services/mention-search.service';
import { MentionsService } from '../../../Monitor/Core/services/mentions.service';
import { SearchResultsService } from '../../../Monitor/Core/services/search-results.service';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { getMonitoringDateQueryInputFolderOptions }
    from '../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { CurationAgentModel } from '../../models/curation-agent.model';

/**
 * One ad hoc run's report: the mentions it curated on the left, and what the run did on the right.
 *
 * The feed is the shared <agpr-mention-search-result>, driven by this panel's own
 * MentionSearchService so it searches independently of whatever the page behind it is showing -
 * the same arrangement the syndicated mentions panel uses.
 */
@Component({
    selector: 'agpr-ad-hoc-report-panel',
    templateUrl: './ad-hoc-report-panel.component.html',
    styleUrls: ['./ad-hoc-report-panel.component.scss'],
    providers: [
        MentionSearchService,
        MentionsService,
        // Bound to this panel's MentionsService rather than the page's, or the insights tab would
        // read whichever mention the results page behind it has focused
        MentionInsightsExplorerService,
        SearchResultsService,
        ToolbarStateService,
        TargetingRhpDialogService,
        TargetingSearchService,
        {
            provide: FilterService,
            useExisting: MentionSearchService
        },
        {
            provide: TargetingRhpContextsService,
            useClass: MentionTargetingRhpContextsService
        }
    ],
    standalone: false
})
export class AdHocReportPanelComponent extends AbstractDialogComponent implements OnInit, OnDestroy {
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverity = messageSeverityEnum;
    readonly dateFilterOptions = getMonitoringDateQueryInputFolderOptions();

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    /** The agent that ran, for the report's Agent section */
    @Input() agent: CurationAgentModel;
    /** The run being reported on */
    @Input() run: AgentAdhocSourceResponse;

    @ViewChild('takeOverPanel') takeOverPanel: TakeOverPanelComponent;
    @ViewChild('rhpTabs') rhpTabs: RightSidebarComponent;

    hideSlideOverlay: boolean = false;
    isLoading: boolean = false;
    focusedMention: Mention;
    mentions: Mention[] = [];
    selectedTabIndex: number = 0;

    /** Same two gates the results page applies to its own tabs */
    mieEnabled: boolean = true;
    hasDiscoverPermission: boolean = false;

    private currentSearchCriteria: MentionSearchCriteriaModel;

    private readonly subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        public mentionSearchService: MentionSearchService,
        private mentionsService: MentionsService,
        private targetingRhpContextsService: TargetingRhpContextsService,
        private rhpDialogService: TargetingRhpDialogService,
        private targetingSearchService: TargetingSearchService,
        private currentUserStateService: CurrentUserStateService,
        private configService: ConfigService,
        private injector: Injector
    ) {
        super(coreDialogService, uuid.v4());
    }

    ngOnInit(): void {
        if (!this.run?.id) {
            return;
        }

        if (this.configService.brandGUIDIsBurrelles() || this.configService.brandGUIDIsRefMedia()) {
            this.mieEnabled = false;
        }

        this.startSearch();

        this.subscriptions.push(
            this.currentUserStateService.hasAnyPermissions([userPermissionsEnum.advancedTargeting])
                .subscribe(hasPermission => this.hasDiscoverPermission = hasPermission),
            this.mentionSearchService.getSearchCriteria().subscribe(criteria => {
                this.currentSearchCriteria = criteria;
                this.entitySearch();
            }),
            this.mentionSearchService.getMentions().subscribe(mentions => this.mentionsService.setMentions(mentions || [])),
            this.mentionsService.getMentions().subscribe(mentions => this.mentions = mentions),
            this.mentionsService.getFocusedMention().subscribe(mention => {
                this.focusedMention = mention;

                if (mention?.author?.uuid) {
                    this.targetingRhpContextsService.setFullProfileContactById(mention.author.uuid);
                } else {
                    this.targetingRhpContextsService.clearContact();
                }

                if (mention?.publication?.uuid) {
                    this.targetingRhpContextsService.setFullProfileOutletById(mention.publication.uuid);
                } else {
                    this.targetingRhpContextsService.clearOutlet();
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.subscriptions.length = 0;
    }

    /**
     * Close runs the panel's own slide-out so it matches the X and click-outside; the dialog is
     * torn down by {@link onVisibleChange} once that animation finishes.
     */
    close(): void {
        if (this.takeOverPanel?.isPanelFullyOpened) {
            this.takeOverPanel.close();

            return;
        }

        this.closeDialog();
    }

    onVisibleChange(isVisible: boolean): void {
        if (!isVisible) {
            this.closeDialog();
        }
    }

    /** The contact and outlet lists search off the feed's criteria, and only while they are open */
    tabChange(tab: number): void {
        this.selectedTabIndex = tab;
        this.entitySearch();
    }

    openEntityTakeover(event: EntityTakeoverModel): void {
        this.rhpDialogService.openTakeOver({
            inputs: {
                contactId: event.contactId,
                outletId: event.outletId,
                entityType: event.entityType,
                isPrivate: event.isPrivate
            }
        });
    }

    openMentionTakeOver(): void {
        const io: DialogIoModel = {
            inputs: {
                focusTab: 'mention',
                fromReports: true
            },
            outputs: {}
        };

        this.coreDialogService.open(MentionDetailsTakeOverPanelComponent, io, this.injector);
    }

    /** Faceted by whichever of the two entity lists is open, as the syndicates panel does it */
    private entitySearch(): void {
        if (!this.currentSearchCriteria?.query?.dateQuery) {
            return;
        }

        const tabs = this.rhpTabs?.tabs?.toArray();
        const contactTabIndex = tabs?.findIndex(tab => tab.id === 'adHocReportRhpTab.contactList');
        const outletTabIndex = tabs?.findIndex(tab => tab.id === 'adHocReportRhpTab.outletList');

        if (this.selectedTabIndex !== contactTabIndex && this.selectedTabIndex !== outletTabIndex) {
            return;
        }

        const search = new TargetingSearch();

        if (this.selectedTabIndex === contactTabIndex) {
            search.searchMode = SearchModeEnum.contact;

            if (!this.currentSearchCriteria.facets.includes(MentionSearchFacet.contactUuid)) {
                this.currentSearchCriteria.facets.push(MentionSearchFacet.contactUuid);
            }

            this.currentSearchCriteria.facets = this.currentSearchCriteria.facets.filter(
                facet => facet !== MentionSearchFacet.outletUuid
            );
        } else {
            search.searchMode = SearchModeEnum.outlet;

            if (!this.currentSearchCriteria.facets.includes(MentionSearchFacet.outletUuid)) {
                this.currentSearchCriteria.facets.push(MentionSearchFacet.outletUuid);
            }

            this.currentSearchCriteria.facets = this.currentSearchCriteria.facets.filter(
                facet => facet !== MentionSearchFacet.contactUuid
            );
        }

        this.currentSearchCriteria.dateFacets = [];

        search.advMonQuery = {
            searchRequestJSON: JSON.stringify(this.currentSearchCriteria.getApiEntity()),
            isSavedSearch: this.currentSearchCriteria.isAccountSearch
        };

        this.targetingSearchService.clearSearch();
        this.targetingSearchService.performSearch(search);
    }

    /**
     * Everything this agent curated in this run, and nothing else. Only the ad hoc filter is set:
     * advanced monitoring ANDs it with the source filter, and a mention carries one or the other.
     */
    private startSearch(): void {
        const criteria = new MentionSearchCriteriaModel();

        // Agents act on saved coverage, so this searches the account rather than new coverage
        criteria.isAccountSearch = true;
        // The run's mentions can be older than any default window, so open on everything
        criteria.query.dateQuery = DateQueryModel.getAllQuery();
        criteria.query.aiAgent = { include: this.agent?.id ? [this.agent.id] : [], exclude: [] };
        criteria.query.aiAgentAdhoc = { include: [this.run.id], exclude: [] };

        this.mentionSearchService.setDateQuery(criteria.query.dateQuery);
        this.mentionSearchService.setSearchCriteria(criteria);
        this.mentionSearchService.search(true, true);
    }
}
